import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {useApp} from "../../app.context";
import {useIntegrator} from "../../integrator.context";
import {hexToRgb, RGB, rgbToHex, TintMaskEngine} from "../../utils/tintMask";
import {encodeGreyscalePngFile} from "../../utils/greyscalePng";
import {ImageMaskMetadata} from "../../models/remoteConstants.model";

// Opening value for the preview colour: the client's Blue team. Only a
// convenient starting point — deliberately NOT a mirror of the game's palette,
// which lives on Client/Assets/Prefabs/Services/TeamsManager.prefab and is the
// client's business. The picker is what makes the preview trustworthy: set it to
// the colour of the team you are actually looking at in game and the two views
// are directly comparable.
const DEFAULT_PREVIEW_HEX = '#0075ff';

// Persisted so the colour survives a page refresh, not just a dialog reopen —
// comparing against the game means reloading the editor after a client rebuild,
// and re-entering the team colour each time is exactly how you end up comparing
// two different colours without noticing.
//
// localStorage rather than branch data on purpose: this is a viewing preference,
// like the grid size and pane widths beside it in the Images tab. Nothing in the
// branch or the exported mask depends on it, and it should not travel between
// users or follow a branch switch.
const PREVIEW_HEX_KEY = 'rift.editor.tintMaskPreviewColour';

function loadPreviewHex(): string {
    const stored = localStorage.getItem(PREVIEW_HEX_KEY);
    // Validate rather than trust: a malformed value would silently render the
    // preview black, which reads as a masking bug rather than a bad preference.
    return stored !== null && /^#[0-9a-fA-F]{6}$/.test(stored) ? stored : DEFAULT_PREVIEW_HEX;
}

const MAX_TARGETS = 8;

// Defaults matching the engine's own, so an unauthored image opens neutral.
const DEFAULT_SHARPNESS = 1;
const DEFAULT_SATURATION = 0;
const DEFAULT_NEUTRAL = 0.02;

interface TintMaskDialogProps {
    // Image key being masked. Its art is read cross-origin to get at the pixels.
    imageKey: string;
    imageUrl: string;
    checkerStyle: React.CSSProperties;
    onClose(): void;
}

type LoadState =
    | {kind: 'loading'}
    // The art loaded but its pixels are unreadable — a cross-origin response
    // without CORS taints the canvas. Same fallback the Workshop's sprite
    // analysis takes, except here there is nothing useful to do without pixels.
    | {kind: 'unreadable'}
    // `pixels` is kept so the source canvas can be painted from an EFFECT rather
    // than from the image's onload. At onload time the canvas does not exist yet:
    // it only renders once this state says 'ready', so the ref is still null and
    // a paint there would be silently dropped, leaving an empty Source panel next
    // to a perfectly good mask.
    | {kind: 'ready'; engine: TintMaskEngine; width: number; height: number; pixels: ImageData};

/**
 * Authoring UI for one image's team-colour mask: pick the source colours that
 * belong to the team, tune the match, and see the result in every team's colour
 * at once before committing.
 *
 * The preview is the point of the dialog. A mask is easy to get subtly wrong —
 * catching a hue that also appears in the terrain, or covering so little that
 * two teams are indistinguishable — and neither shows up in the mask itself,
 * only in the tinted result.
 */
function TintMaskDialog(props: TintMaskDialogProps) {
    const {uploadMask, removeMask, setNotification, confirm} = useApp();
    const {state} = useIntegrator();

    const existing = state.images?.[props.imageKey]?.mask;

    const [load, setLoad] = useState<LoadState>({kind: 'loading'});
    const [targets, setTargets] = useState<RGB[]>(
        () => existing?.targets?.map(hexToRgb) ?? []);
    const [sharpness, setSharpness] = useState(existing?.sharpness ?? DEFAULT_SHARPNESS);
    const [saturation, setSaturation] = useState(existing?.saturation ?? DEFAULT_SATURATION);
    const [neutral, setNeutral] = useState(existing?.neutral ?? DEFAULT_NEUTRAL);
    const [gain, setGain] = useState(existing?.gain ?? 1);
    const [picking, setPicking] = useState(false);
    const [saving, setSaving] = useState(false);
    const [previewHex, setPreviewHex] = useState(loadPreviewHex);
    // Texel the pointer is over, in image coordinates. Drives the readout, which
    // exists so the preview can be compared against the game NUMERICALLY —
    // eyeballing two renders at different scales cannot settle whether the shader
    // and this agree.
    const [probe, setProbe] = useState<{x: number; y: number} | undefined>(undefined);

    const sourceCanvas = useRef<HTMLCanvasElement | null>(null);
    const maskCanvas = useRef<HTMLCanvasElement | null>(null);
    const previewCanvas = useRef<HTMLCanvasElement | null>(null);
    // Last buffer handed to the Result canvas, kept so the readout can index it
    // directly rather than reading pixels back out of the canvas.
    const previewBuffer = useRef<Uint8ClampedArray | null>(null);

    // ---- load the art ------------------------------------------------------
    // crossOrigin so the pixels are readable; the blob host serves the CORS
    // header (this is the same load the Workshop's sprite analysis relies on).
    useEffect(() => {
        let cancelled = false;
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => {
            if (cancelled) return;
            const width = image.naturalWidth;
            const height = image.naturalHeight;
            if (width <= 0 || height <= 0) {
                setLoad({kind: 'unreadable'});
                return;
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const context = canvas.getContext('2d', {willReadFrequently: true});
            if (context === null) {
                setLoad({kind: 'unreadable'});
                return;
            }
            context.drawImage(image, 0, 0);

            let pixels: ImageData;
            try {
                pixels = context.getImageData(0, 0, width, height);
            } catch {
                setLoad({kind: 'unreadable'});   // tainted canvas
                return;
            }

            const engine = new TintMaskEngine(width, height, pixels.data);
            setLoad({kind: 'ready', engine, width, height, pixels});

            // Nothing authored yet: open on the art's most colourful pixel, which
            // is the team colour far more often than not.
            setTargets(current => {
                if (current.length > 0) return current;
                const suggested = engine.suggestTarget();
                return suggested === null ? [] : [suggested];
            });
        };
        image.onerror = () => {
            if (!cancelled) setLoad({kind: 'unreadable'});
        };
        image.src = props.imageUrl;

        return () => {
            cancelled = true;
        };
    }, [props.imageUrl]);

    // ---- mask + previews ---------------------------------------------------
    const mask = useMemo(() => {
        if (load.kind !== 'ready') return undefined;
        return load.engine.computeMask({
            targets,
            sharpness,
            saturationInfluence: saturation,
            neutralThreshold: neutral,
        });
    }, [load, targets, sharpness, saturation, neutral]);

    const coverage = useMemo(
        () => (load.kind === 'ready' && mask !== undefined ? load.engine.coverage(mask) : 0),
        [load, mask]);

    // Paint the source once it exists in the DOM. Separate from the mask paint
    // below because it depends only on the art, and it must not be redone on
    // every slider movement — the click-to-pick handler reads its pixels back.
    useEffect(() => {
        if (load.kind !== 'ready') return;

        const source = sourceCanvas.current;
        if (source === null) return;

        source.width = load.width;
        source.height = load.height;
        source.getContext('2d')?.putImageData(load.pixels, 0, 0);
    }, [load]);

    // Paint the mask and the tinted preview. Straight to canvases in an effect
    // rather than through React state: these are full-size pixel buffers that
    // change on every slider movement, and nothing else needs to see them.
    useEffect(() => {
        if (load.kind !== 'ready' || mask === undefined) return;
        const {engine, width, height} = load;

        const maskTarget = maskCanvas.current;
        if (maskTarget !== null) {
            maskTarget.width = width;
            maskTarget.height = height;
            const image = new ImageData(width, height);
            for (let i = 0, p = 0; i < width * height; i++, p += 4) {
                // Drawn as opaque grey so the checkerboard doesn't read as
                // coverage — this view is about WHERE the mask is, not the art.
                image.data[p] = image.data[p + 1] = image.data[p + 2] = mask[i];
                image.data[p + 3] = 255;
            }
            maskTarget.getContext('2d')?.putImageData(image, 0, 0);
        }

        const tinted = engine.renderPreview(mask, hexToRgb(previewHex), gain);
        previewBuffer.current = tinted;

        const previewTarget = previewCanvas.current;
        if (previewTarget !== null) {
            previewTarget.width = width;
            previewTarget.height = height;
            const preview = new ImageData(width, height);
            preview.data.set(tinted);
            previewTarget.getContext('2d')?.putImageData(preview, 0, 0);
        }
    }, [load, mask, gain, previewHex]);

    // Everything about one texel: what the art holds there, how much of it the
    // mask claims, and what the shader should therefore draw. Sample the same
    // point in game and the three numbers say which stage disagrees.
    const readout = useMemo(() => {
        if (load.kind !== 'ready' || mask === undefined || probe === undefined) return undefined;

        const index = probe.y * load.width + probe.x;
        const p = index * 4;
        const source = load.pixels.data;
        const tinted = previewBuffer.current;

        return {
            x: probe.x,
            y: probe.y,
            source: rgbToHex({r: source[p] / 255, g: source[p + 1] / 255, b: source[p + 2] / 255}),
            sourceAlpha: source[p + 3],
            coverage: mask[index],
            result: tinted === null
                ? undefined
                : rgbToHex({r: tinted[p] / 255, g: tinted[p + 1] / 255, b: tinted[p + 2] / 255}),
        };
        // previewHex/gain don't appear in the body but do change previewBuffer,
        // which is a ref and so can't trigger this on its own.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load, mask, probe, previewHex, gain]);

    // All three canvases show the same texel grid, so one handler serves them.
    function probeAt(event: React.MouseEvent<HTMLCanvasElement>): void {
        const canvas = event.currentTarget;
        const rect = canvas.getBoundingClientRect();
        const x = Math.floor(((event.clientX - rect.left) / rect.width) * canvas.width);
        const y = Math.floor(((event.clientY - rect.top) / rect.height) * canvas.height);

        setProbe(x < 0 || y < 0 || x >= canvas.width || y >= canvas.height
            ? undefined
            : {x, y});
    }

    // Only a complete colour is stored, so a half-typed hex can't be what comes
    // back after a refresh.
    function changePreviewHex(hex: string): void {
        setPreviewHex(hex);
        if (/^#[0-9a-fA-F]{6}$/.test(hex)) localStorage.setItem(PREVIEW_HEX_KEY, hex);
    }

    // ---- target editing ----------------------------------------------------
    const addTarget = useCallback((colour: RGB) => {
        setTargets(current => (current.length >= MAX_TARGETS ? current : [...current, colour]));
    }, []);

    const pickFromSource = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
        const canvas = sourceCanvas.current;
        if (!picking || canvas === null) return;

        const rect = canvas.getBoundingClientRect();
        const x = Math.floor(((event.clientX - rect.left) / rect.width) * canvas.width);
        const y = Math.floor(((event.clientY - rect.top) / rect.height) * canvas.height);
        const pixel = canvas.getContext('2d')?.getImageData(x, y, 1, 1).data;
        if (pixel === undefined) return;

        addTarget({r: pixel[0] / 255, g: pixel[1] / 255, b: pixel[2] / 255});
    }, [picking, addTarget]);

    // ---- commit ------------------------------------------------------------
    async function save(): Promise<void> {
        if (load.kind !== 'ready' || mask === undefined) return;

        if (coverage === 0) {
            setNotification({
                title: 'Nothing to mask',
                message: 'This mask covers no visible pixels, so it would have no effect. '
                    + 'Pick a colour that appears in the art, or lower the neutral threshold.',
            });
            return;
        }

        setSaving(true);
        try {
            const file = await encodeGreyscalePngFile(
                mask, load.width, load.height, `${props.imageKey}_mask.png`);

            const settings: Omit<ImageMaskMetadata, 'fileName'> = {
                gain,
                targets: targets.map(rgbToHex),
                sharpness,
                saturation,
                neutral,
            };

            if (await uploadMask(props.imageKey, file, settings)) props.onClose();
        } catch (e) {
            setNotification({
                title: 'Could not build the mask',
                message: e instanceof Error ? e.message : String(e),
            });
        } finally {
            setSaving(false);
        }
    }

    async function remove(): Promise<void> {
        const proceed = await confirm({
            title: 'Remove team colouring?',
            body: <p>This image will stop taking team colours and will render exactly as authored.</p>,
            confirmText: 'Remove mask',
            danger: true,
        });
        if (!proceed) return;

        removeMask(props.imageKey);
        props.onClose();
    }

    const previewStyle: React.CSSProperties = {
        ...props.checkerStyle,
        width: '100%',
        aspectRatio: '1 / 1',
        objectFit: 'contain',
        // Point-sampled on purpose for Source and Mask: it keeps single-texel mask
        // speckle visible instead of smoothing it into a plausible-looking grey.
        imageRendering: 'pixelated',
    };

    // The Result panel does NOT get that. Point sampling skips texels rather than
    // averaging them, which preserves the brightest ones and makes this panel read
    // lighter than the game — the game minifies with bilinear filtering and
    // averages the neighbours in. Smoothing here removes that bias so a
    // side-by-side against the game compares like with like.
    const resultStyle: React.CSSProperties = {...previewStyle, imageRendering: 'auto'};

    // Portal to the body for the same reason the reward timeline does: an
    // opacity-styled ancestor would otherwise drag the dialog into its stacking
    // context and wash it out.
    return createPortal(
        <div className={'layout vertical overlay'} onClick={props.onClose}>
            <div className={'layout vertical gap box'}
                 style={{padding: 15, gap: 12, width: 760, maxHeight: '90vh', overflowY: 'auto'}}
                 onClick={e => e.stopPropagation()}>

                <div className={'layout horizontal center stretch'} style={{gap: 10}}>
                    <p className={'text header'}>Team colour mask</p>
                    <button onClick={props.onClose} title={'Close'}>
                        <span className={'fa-solid fa-xmark'}/>
                    </button>
                </div>

                {load.kind === 'loading' && <p className={'muted-text'}>Reading the image…</p>}

                {load.kind === 'unreadable' && (
                    <p className={'muted-text'}>
                        This image's pixels could not be read, so a mask can't be derived from it.
                        That usually means the asset host did not return CORS headers for it.
                    </p>
                )}

                {load.kind === 'ready' && (
                    <>
                        <p className={'muted-text'} style={{fontSize: 13}}>
                            Pick the colours in this art that belong to the owning team. In game the
                            covered region is recoloured to the team's colour at each pixel's own
                            brightness, so shading and detail survive — the preview is exactly what
                            the shader draws, so set it to the colour of the team you're looking at
                            in game and the two are directly comparable.
                        </p>

                        {/* Source, mask, result */}
                        <div className={'layout horizontal gap'} style={{gap: 12}}>
                            <div className={'layout vertical center'} style={{gap: 4, flex: 1}}>
                                <p className={'muted-text'}>
                                    Source {picking && <span>— click to add a colour</span>}
                                </p>
                                <canvas ref={sourceCanvas}
                                        onClick={pickFromSource}
                                        onMouseMove={probeAt}
                                        onMouseLeave={() => setProbe(undefined)}
                                        style={{...previewStyle, cursor: picking ? 'crosshair' : 'default'}}/>
                            </div>
                            <div className={'layout vertical center'} style={{gap: 4, flex: 1}}>
                                <p className={'muted-text'}>Mask ({Math.round(coverage * 100)}% covered)</p>
                                <canvas ref={maskCanvas}
                                        onMouseMove={probeAt}
                                        onMouseLeave={() => setProbe(undefined)}
                                        style={{...previewStyle, background: '#000'}}/>
                            </div>
                            <div className={'layout vertical center'} style={{gap: 4, flex: 1}}>
                                <p className={'muted-text'}>Result</p>
                                <canvas ref={previewCanvas}
                                        onMouseMove={probeAt}
                                        onMouseLeave={() => setProbe(undefined)}
                                        style={resultStyle}/>
                            </div>
                        </div>

                        {/* Per-texel readout. Fixed height so the layout doesn't
                            twitch as the pointer enters and leaves a canvas.
                            The explicit colour is needed: App.css puts `color:
                            white` on `p`, not on `body`, so bare spans inherit the
                            browser default black and vanish against this panel. */}
                        <div className={'layout horizontal center'}
                             style={{gap: 12, height: 20, color: 'white',
                                 fontFamily: 'monospace', fontSize: 12}}>
                            {readout === undefined
                                ? <span className={'muted-text'}>
                                      Hover any panel to read one texel — compare the numbers with the game
                                  </span>
                                : <>
                                    <span className={'muted-text'}>{readout.x}, {readout.y}</span>
                                    <span>source <strong>{readout.source}</strong></span>
                                    {readout.sourceAlpha < 255 &&
                                        <span className={'muted-text'}>alpha {readout.sourceAlpha}</span>}
                                    <span>mask <strong>{readout.coverage}</strong>
                                        <span className={'muted-text'}>
                                            /255 ({Math.round(readout.coverage / 2.55)}%)
                                        </span>
                                    </span>
                                    {readout.result !== undefined && <>
                                        <span>result <strong>{readout.result}</strong></span>
                                        <span style={{
                                            width: 14, height: 14, borderRadius: 3,
                                            backgroundColor: readout.result,
                                            border: '1px solid rgba(255,255,255,0.3)',
                                        }}/>
                                    </>}
                                  </>}
                        </div>

                        {/* Preview colour — a viewing control, not authored data:
                            it is never saved, and the client uses its own team
                            palette. */}
                        <div className={'layout horizontal center'} style={{gap: 8}}>
                            <p style={{width: 150}}>Preview colour</p>
                            <input type={'color'}
                                   value={previewHex}
                                   title={'The team colour to preview with — not saved'}
                                   onChange={e => changePreviewHex(e.target.value)}/>
                            <input type={'text'}
                                   spellCheck={false}
                                   value={previewHex}
                                   style={{width: 90}}
                                   title={'Paste the exact team colour from the game to compare'}
                                   onChange={e => changePreviewHex(e.target.value.trim())}/>
                            <span className={'muted-text'} style={{fontSize: 12}}>
                                not saved — the game uses its own team palette
                            </span>
                        </div>

                        {/* Targets */}
                        <div className={'layout horizontal center'} style={{gap: 8, flexWrap: 'wrap'}}>
                            <p>Source colours <span className={'muted-text'}>{targets.length}/{MAX_TARGETS}</span></p>
                            {targets.map((target, index) => (
                                <div key={index} className={'layout horizontal center'} style={{gap: 2}}>
                                    <input type={'color'}
                                           value={rgbToHex(target)}
                                           title={`Colour ${index + 1}`}
                                           onChange={e => setTargets(current => current.map(
                                               (c, i) => (i === index ? hexToRgb(e.target.value) : c)))}/>
                                    <button title={'Remove this colour'}
                                            onClick={() => setTargets(current => current.filter((_, i) => i !== index))}>
                                        <span className={'fa-solid fa-xmark'}/>
                                    </button>
                                </div>
                            ))}
                            <button onClick={() => setPicking(!picking)}
                                    disabled={targets.length >= MAX_TARGETS && !picking}
                                    title={'Add a colour by clicking the source image'}>
                                {picking ? 'Done picking' : 'Pick from image'}
                            </button>
                        </div>

                        {/* Tuning */}
                        <div className={'layout vertical gap'} style={{gap: 6}}>
                            <Slider label={'Sharpness'} value={sharpness} min={0.25} max={64} step={0.25}
                                    onChange={setSharpness}
                                    hint={'Higher tightens the match to near-exact hues.'}/>
                            <Slider label={'Saturation influence'} value={saturation} min={0} max={1} step={0.01}
                                    onChange={setSaturation}
                                    hint={'How much a pixel\'s own colourfulness reduces its coverage.'}/>
                            <Slider label={'Neutral threshold'} value={neutral} min={0} max={0.8} step={0.005}
                                    onChange={setNeutral}
                                    hint={'Colours fainter than this count as grey and are left alone.'}/>
                            <div className={'layout horizontal center'} style={{gap: 8}}>
                                <Slider label={'Brightness gain'} value={gain} min={0} max={8} step={0.05}
                                        onChange={setGain}
                                        hint={'Lifts a dark region so it can carry a bright team colour.'}/>
                                <button title={'Set the gain so the brightest covered pixel reaches white'}
                                        onClick={() => {
                                            if (mask === undefined) return;
                                            setGain(Math.min(8, load.engine.suggestGain(mask)));
                                        }}>
                                    Auto
                                </button>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className={'layout horizontal center'} style={{gap: 8}}>
                            <button onClick={save} disabled={saving || coverage === 0}>
                                {saving ? 'Saving…' : existing === undefined ? 'Attach mask' : 'Replace mask'}
                            </button>
                            {existing !== undefined && (
                                <button onClick={remove} disabled={saving}>Remove mask</button>
                            )}
                            <div className={'spacer'} style={{flex: 1}}/>
                            <button onClick={props.onClose} disabled={saving}>Cancel</button>
                        </div>
                    </>
                )}
            </div>
        </div>,
        document.body,
    );
}

interface SliderProps {
    label: string;
    hint: string;
    value: number;
    min: number;
    max: number;
    step: number;
    onChange(value: number): void;
}

function Slider(props: SliderProps) {
    return (
        <div className={'layout horizontal center'} style={{gap: 8, flex: 1}}>
            <p style={{width: 150}}>{props.label}</p>
            <input type={'range'}
                   min={props.min}
                   max={props.max}
                   step={props.step}
                   value={props.value}
                   title={props.hint}
                   onChange={e => props.onChange(Number(e.target.value))}
                   style={{flex: 1}}/>
            <p className={'muted-text'} style={{width: 50, textAlign: 'right'}}>
                {props.value.toFixed(2)}
            </p>
        </div>
    );
}

export default TintMaskDialog;
